/**
 * ClusterGraph — Original cluster visualization with 4 colored clusters in a 2x2 grid.
 * Each cluster has a hub node with child nodes orbiting in a radial layout.
 * Now includes d3-force simulation and draggable nodes.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Expand, Maximize2 } from 'lucide-react';
import { forceSimulation, forceLink, forceManyBody, forceX, forceY, forceCollide } from 'd3';
import type { Simulation } from 'd3-force';
import type { Dataset, ClusterId, GraphEdgeDef } from '@/data/clusterscope-data';

const easeOut: [number, number, number, number] = [0.16, 1, 0.3, 1];

const VIEW_W = 1600;
const VIEW_H = 1000;

const CLUSTER_QUADRANTS = [
  { x: VIEW_W * 0.25, y: VIEW_H * 0.25 },
  { x: VIEW_W * 0.75, y: VIEW_H * 0.25 },
  { x: VIEW_W * 0.25, y: VIEW_H * 0.75 },
  { x: VIEW_W * 0.75, y: VIEW_H * 0.75 },
];

const COLOR_ORDER = ['#a78bfa', '#14e0b0', '#f472b6', '#fb923c'];

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function bezierPath(x1: number, y1: number, x2: number, y2: number, curveOffset: number) {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len;
  const ny = dx / len;
  const cx = mx + nx * curveOffset;
  const cy = my + ny * curveOffset;
  return `M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`;
}

interface Positions {
  [id: string]: { x: number; y: number };
}

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  title: string;
  lines: { label: string; value: string; color?: string }[];
}

interface SimNode {
  id: string;
  x: number;
  y: number;
  fx?: number | null;
  fy?: number | null;
}

interface ClusterGraphProps {
  dataset: Dataset;
  selectedClusterId: ClusterId | null;
  onSelectCluster: (id: ClusterId | null) => void;
  selectedNodeId: string | null;
  onSelectNode: (id: string | null) => void;
}

export default function ClusterGraph({
  dataset,
  selectedClusterId,
  onSelectCluster,
  selectedNodeId,
  onSelectNode,
}: ClusterGraphProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const rafRef = useRef<number>(0);
  const targetViewRef = useRef({ panX: 0, panY: 0, zoom: 1 });
  const currentViewRef = useRef({ panX: 0, panY: 0, zoom: 1 });
  const simRef = useRef<Simulation<SimNode, undefined> | null>(null);

  const [view, setView] = useState({ panX: 0, panY: 0, zoom: 1 });
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [hoveredEdgeKey, setHoveredEdgeKey] = useState<string | null>(null);
  const [isPanning, setIsPanning] = useState(false);
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    x: 0,
    y: 0,
    title: '',
    lines: [],
  });

  // Sort clusters into 2x2 grid based on color order
  const sortedClusters = useMemo(() => {
    const clusters = dataset.clusters.slice(0, 4);
    return [...clusters].sort((a, b) => {
      const idxA = COLOR_ORDER.indexOf(a.color);
      const idxB = COLOR_ORDER.indexOf(b.color);
      if (idxA === -1 && idxB === -1) return 0;
      if (idxA === -1) return 1;
      if (idxB === -1) return -1;
      return idxA - idxB;
    });
  }, [dataset.clusters]);

  // Compute static layout positions
  const staticPositions = useMemo<Positions>(() => {
    const pos: Positions = {};

    sortedClusters.forEach((cluster, qi) => {
      const center = CLUSTER_QUADRANTS[qi];
      const clusterNodes = dataset.graphNodes.filter((n) => n.clusterId === cluster.id);
      const hub = clusterNodes.find((n) => n.isHub);
      const children = clusterNodes.filter((n) => !n.isHub);

      if (hub) {
        pos[hub.id] = { x: center.x, y: center.y };
      }

      const orbitRadius = 130;
      children.forEach((child, i) => {
        const angle = (i / Math.max(children.length, 1)) * Math.PI * 2 - Math.PI / 2;
        pos[child.id] = {
          x: center.x + Math.cos(angle) * orbitRadius,
          y: center.y + Math.sin(angle) * orbitRadius,
        };
      });
    });

    return pos;
  }, [dataset, sortedClusters]);

  // Mutable positions state with d3-force simulation
  const [positions, setPositions] = useState<Positions>(staticPositions);

  // Reset positions when dataset changes
  useEffect(() => {
    setPositions(staticPositions);
  }, [staticPositions]);

  // Run d3-force simulation
  useEffect(() => {
    // Stop old simulation
    simRef.current?.stop();

    const nodes: SimNode[] = dataset.graphNodes.map((n) => ({
      id: n.id,
      x: staticPositions[n.id]?.x ?? 0,
      y: staticPositions[n.id]?.y ?? 0,
    }));

    const links = dataset.graphEdges.map((e) => ({
      source: e.source,
      target: e.target,
    }));

    const sim = forceSimulation(nodes)
      .force(
        'link',
        forceLink(links)
          .id((d: any) => d.id)
          .strength(0.3)
          .distance(150),
      )
      .force('charge', forceManyBody().strength(-120))
      .force(
        'x',
        forceX((d: any) => staticPositions[d.id]?.x ?? 0).strength(0.08),
      )
      .force(
        'y',
        forceY((d: any) => staticPositions[d.id]?.y ?? 0).strength(0.08),
      )
      .force(
        'collide',
        forceCollide().radius((d: any) => {
          const node = dataset.graphNodeMap[d.id];
          return node?.isHub ? 35 : 16;
        }),
      )
      .on('tick', () => {
        const newPos: Positions = {};
        nodes.forEach((n) => {
          newPos[n.id] = { x: n.x, y: n.y };
        });
        setPositions(newPos);
      });

    simRef.current = sim;

    return () => {
      sim.stop();
    };
  }, [dataset, sortedClusters, staticPositions]);

  // Smooth camera animation loop
  useEffect(() => {
    const animate = () => {
      const target = targetViewRef.current;
      const current = currentViewRef.current;
      const dx = target.panX - current.panX;
      const dy = target.panY - current.panY;
      const dz = target.zoom - current.zoom;
      const threshold = 0.0005;
      if (Math.abs(dx) < threshold && Math.abs(dy) < threshold && Math.abs(dz) < threshold) {
        currentViewRef.current = { ...target };
      } else {
        currentViewRef.current = {
          panX: lerp(current.panX, target.panX, 0.12),
          panY: lerp(current.panY, target.panY, 0.12),
          zoom: lerp(current.zoom, target.zoom, 0.12),
        };
      }
      setView({ ...currentViewRef.current });
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const setTargetView = useCallback(
    (
      updater:
        | ((v: { panX: number; panY: number; zoom: number }) => { panX: number; panY: number; zoom: number })
        | { panX: number; panY: number; zoom: number },
    ) => {
      if (typeof updater === 'function') {
        targetViewRef.current = updater(targetViewRef.current);
      } else {
        targetViewRef.current = updater;
      }
    },
    [],
  );

  const getRatio = useCallback(() => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || !rect.width) return 1;
    return VIEW_W / rect.width;
  }, []);

  // Native non-passive wheel listener for smooth zoom
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;
      const ratio = VIEW_W / rect.width;
      const mouseX = (e.clientX - rect.left) * ratio;
      const mouseY = (e.clientY - rect.top) * ratio;

      const zoomFactor = e.deltaY < 0 ? 1.08 : 0.92;
      const oldZoom = targetViewRef.current.zoom;
      const newZoom = Math.min(2.8, Math.max(0.35, oldZoom * zoomFactor));

      const worldX = (mouseX - targetViewRef.current.panX) / oldZoom;
      const worldY = (mouseY - targetViewRef.current.panY) / oldZoom;
      const newPanX = mouseX - worldX * newZoom;
      const newPanY = mouseY - worldY * newZoom;

      setTargetView({ panX: newPanX, panY: newPanY, zoom: newZoom });
    };
    container.addEventListener('wheel', handler, { passive: false });
    return () => container.removeEventListener('wheel', handler);
  }, [setTargetView]);

  const handleBackgroundPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== 0) return;
      setIsPanning(true);
      const startX = e.clientX;
      const startY = e.clientY;
      let lastX = e.clientX;
      let lastY = e.clientY;
      let moved = false;

      const onMove = (ev: PointerEvent) => {
        if (Math.abs(ev.clientX - startX) > 3 || Math.abs(ev.clientY - startY) > 3) moved = true;
        const ratio = svgRef.current ? VIEW_W / svgRef.current.getBoundingClientRect().width : 1;
        const dx = (ev.clientX - lastX) * ratio;
        const dy = (ev.clientY - lastY) * ratio;
        lastX = ev.clientX;
        lastY = ev.clientY;
        setTargetView((v) => ({ ...v, panX: v.panX + dx, panY: v.panY + dy }));
      };

      const onUp = () => {
        setIsPanning(false);
        if (!moved) {
          onSelectNode(null);
          onSelectCluster(null);
        }
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [setTargetView, onSelectNode, onSelectCluster],
  );

  const handleNodePointerDown = useCallback(
    (nodeId: string) => (e: React.PointerEvent) => {
      e.stopPropagation();
      let moved = false;
      let lastX = e.clientX;
      let lastY = e.clientY;

      const onMove = (ev: PointerEvent) => {
        if (Math.abs(ev.clientX - lastX) > 2 || Math.abs(ev.clientY - lastY) > 2) moved = true;

        const sim = simRef.current;
        if (sim) {
          const simNode = sim.nodes().find((n: SimNode) => n.id === nodeId);
          if (simNode) {
            const ratio = svgRef.current ? VIEW_W / svgRef.current.getBoundingClientRect().width : 1;
            const currentZoom = currentViewRef.current.zoom;
            const dx = ((ev.clientX - lastX) * ratio) / currentZoom;
            const dy = ((ev.clientY - lastY) * ratio) / currentZoom;

            simNode.fx = (simNode.fx ?? simNode.x) + dx;
            simNode.fy = (simNode.fy ?? simNode.y) + dy;
            sim.alpha(0.3).restart();
          }
        }

        lastX = ev.clientX;
        lastY = ev.clientY;
      };

      const onUp = () => {
        if (!moved) {
          const node = dataset.graphNodeMap[nodeId];
          if (node) {
            onSelectNode(node.id);
            onSelectCluster(node.clusterId);
          }
        }
        // Release the node
        const sim = simRef.current;
        if (sim) {
          const simNode = sim.nodes().find((n: SimNode) => n.id === nodeId);
          if (simNode) {
            simNode.fx = null;
            simNode.fy = null;
          }
        }
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [dataset, onSelectCluster, onSelectNode],
  );

  const resetView = useCallback(() => {
    setTargetView({ panX: 0, panY: 0, zoom: 1 });
    onSelectNode(null);
  }, [setTargetView, onSelectNode]);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => undefined);
    } else {
      containerRef.current?.requestFullscreen?.().catch(() => undefined);
    }
  }, []);

  const handleDoubleClick = useCallback(() => {
    resetView();
  }, [resetView]);

  // Tooltip helpers
  const showNodeTooltip = useCallback(
    (nodeId: string, clientX: number, clientY: number) => {
      const node = dataset.graphNodeMap[nodeId];
      if (node) {
        const lines = [
          { label: 'Address', value: node.label },
          { label: 'PnL', value: node.realizedPnl ?? '—', color: node.realizedPnl?.startsWith('-') ? '#fb923c' : '#14e0b0' },
          { label: 'Win Rate', value: `${node.winRate ?? 0}%` },
          { label: 'Transactions', value: `${node.tradeCount ?? 0}` },
          { label: 'Last Activity', value: node.lastActive ?? '—' },
        ];
        setTooltip({
          visible: true,
          x: clientX + 16,
          y: clientY - 10,
          title: node.label,
          lines,
        });
      }
    },
    [dataset],
  );

  const showEdgeTooltip = useCallback(
    (edge: GraphEdgeDef, clientX: number, clientY: number) => {
      const sourceNode = dataset.graphNodeMap[edge.source];
      const targetNode = dataset.graphNodeMap[edge.target];
      const sameCluster = sourceNode?.clusterId === targetNode?.clusterId;
      const lines = [
        { label: 'Type', value: sameCluster ? 'Internal Cluster Relationship' : 'Cross-Cluster Connection' },
        { label: 'Source', value: sourceNode?.label ?? edge.source },
        { label: 'Target', value: targetNode?.label ?? edge.target },
        { label: 'Cluster', value: dataset.clusterMap[edge.clusterId]?.name ?? edge.clusterId },
      ];
      setTooltip({
        visible: true,
        x: clientX + 16,
        y: clientY - 10,
        title: 'Connection',
        lines,
      });
    },
    [dataset],
  );

  const hideTooltip = useCallback(() => {
    setTooltip((t) => ({ ...t, visible: false }));
  }, []);

  // Compute opacity for dimming unselected nodes/edges
  const connectedIds = useMemo(() => {
    const set = new Set<string>();
    if (hoveredNodeId || selectedNodeId) {
      const targetId = selectedNodeId || hoveredNodeId;
      for (const edge of dataset.graphEdges) {
        if (edge.source === targetId) set.add(edge.target);
        if (edge.target === targetId) set.add(edge.source);
      }
      if (targetId) set.add(targetId);
    }
    return set;
  }, [hoveredNodeId, selectedNodeId, dataset]);

  const nodeOpacity = (nodeId: string, clusterId: ClusterId) => {
    if (selectedNodeId || hoveredNodeId) {
      return connectedIds.has(nodeId) ? 1 : 0.15;
    }
    if (selectedClusterId && selectedClusterId !== clusterId) {
      return 0.25;
    }
    return 0.85;
  };

  const edgeOpacity = (source: string, target: string) => {
    if (selectedNodeId || hoveredNodeId) {
      const targetId = selectedNodeId || hoveredNodeId;
      return source === targetId || target === targetId ? 1 : 0.08;
    }
    return 0.25;
  };

  const getNodeRadius = useCallback((nodeId: string) => {
    const node = dataset.graphNodeMap[nodeId];
    if (node?.isHub) return 35;
    return 16;
  }, [dataset]);

  // Build cluster-index map for staggered animation
  const clusterIndexMap = useMemo(() => {
    const map: Record<string, number> = {};
    sortedClusters.forEach((c, i) => {
      map[c.id] = i;
    });
    return map;
  }, [sortedClusters]);

  // Cross-cluster edges between hubs
  const crossClusterEdges = useMemo(() => {
    const hubs = sortedClusters
      .map((cluster) => dataset.graphNodes.find((n) => n.clusterId === cluster.id && n.isHub))
      .filter(Boolean) as Array<{ id: string }>;

    const edges: Array<{ source: string; target: string }> = [];
    const maxEdges = Math.min(4, hubs.length);
    for (let i = 0; i < maxEdges; i++) {
      const next = (i + 1) % hubs.length;
      edges.push({ source: hubs[i].id, target: hubs[next].id });
    }
    return edges;
  }, [sortedClusters, dataset]);

  return (
    <div ref={containerRef} className="relative h-full w-full overflow-hidden bg-background">
      <div className="pointer-events-none absolute inset-0 bg-grid-pattern opacity-[0.18]" />

      {/* Tooltip overlay */}
      <AnimatePresence>
        {tooltip.visible && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.15 }}
            className="pointer-events-none absolute z-50 min-w-[180px] max-w-[260px] rounded-xl border border-white/10 bg-[hsl(228,22%,7%)]/95 p-3.5 shadow-2xl backdrop-blur-xl"
            style={{
              left: Math.min(tooltip.x, containerRef.current ? containerRef.current.clientWidth - 280 : tooltip.x),
              top: tooltip.y,
            }}
          >
            <div className="mb-2 text-xs font-extrabold text-foreground">{tooltip.title}</div>
            <div className="space-y-1">
              {tooltip.lines.map((line) => (
                <div key={line.label} className="flex items-center justify-between gap-4 text-[11px]">
                  <span className="text-muted-foreground">{line.label}</span>
                  <span className="font-semibold text-foreground" style={line.color ? { color: line.color } : undefined}>
                    {line.value}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <svg
        ref={svgRef}
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        className={`h-full w-full touch-none select-none ${isPanning ? 'cursor-grabbing' : 'cursor-grab'}`}
        onPointerDown={handleBackgroundPointerDown}
        onDoubleClick={handleDoubleClick}
      >
        <defs>
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="8" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <g transform={`translate(${view.panX} ${view.panY}) scale(${view.zoom})`}>
          {/* Edges */}
          {dataset.graphEdges.map((edge, i) => {
            const s = positions[edge.source];
            const t = positions[edge.target];
            if (!s || !t) return null;
            const d = bezierPath(s.x, s.y, t.x, t.y, ((i % 2) * 2 - 1) * 20);
            const opacity = edgeOpacity(edge.source, edge.target);
            const cluster = dataset.clusterMap[edge.clusterId];
            const color = cluster?.color ?? '#a1a1aa';

            return (
              <motion.path
                key={`edge-${edge.source}-${edge.target}`}
                d={d}
                fill="none"
                stroke={color}
                strokeWidth={2.5}
                strokeOpacity={opacity}
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: opacity }}
                transition={{ duration: 0.6, delay: (clusterIndexMap[edge.clusterId] ?? 0) * 0.2 + 0.1, ease: easeOut }}
                onMouseEnter={(e) => {
                  setHoveredEdgeKey(`edge-${edge.source}-${edge.target}`);
                  showEdgeTooltip(edge, e.clientX, e.clientY);
                }}
                onMouseMove={(e) => {
                  showEdgeTooltip(edge, e.clientX, e.clientY);
                }}
                onMouseLeave={() => {
                  setHoveredEdgeKey(null);
                  hideTooltip();
                }}
                style={{ cursor: 'pointer' }}
              />
            );
          })}

          {/* Cross-cluster edges */}
          {crossClusterEdges.map((edge, i) => {
            const s = positions[edge.source];
            const t = positions[edge.target];
            if (!s || !t) return null;
            const d = bezierPath(s.x, s.y, t.x, t.y, ((i % 2) * 2 - 1) * 20);

            return (
              <motion.path
                key={`cross-${edge.source}-${edge.target}`}
                d={d}
                fill="none"
                stroke="#a1a1aa"
                strokeWidth={2.5}
                strokeOpacity={0.15}
                strokeDasharray="4 4"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 0.15 }}
                transition={{ duration: 0.6, delay: 0.8 + i * 0.1, ease: easeOut }}
              />
            );
          })}

          {/* Nodes */}
          {dataset.graphNodes.map((node, i) => {
            const pos = positions[node.id];
            if (!pos) return null;
            const cluster = dataset.clusterMap[node.clusterId];
            const color = cluster?.color ?? '#a1a1aa';
            const ci = clusterIndexMap[node.clusterId] ?? 0;
            const delay = ci * 0.2 + 0.2 + (i % 5) * 0.03;
            const radius = getNodeRadius(node.id);
            const isHovered = hoveredNodeId === node.id;
            const isSelectedNode = selectedNodeId === node.id;
            const opacity = nodeOpacity(node.id, node.clusterId);

            return (
              <g
                key={node.id}
                transform={`translate(${pos.x} ${pos.y})`}
                onPointerDown={handleNodePointerDown(node.id)}
                onMouseEnter={(e) => {
                  setHoveredNodeId(node.id);
                  showNodeTooltip(node.id, e.clientX, e.clientY);
                }}
                onMouseMove={(e) => {
                  showNodeTooltip(node.id, e.clientX, e.clientY);
                }}
                onMouseLeave={() => {
                  setHoveredNodeId(null);
                  hideTooltip();
                }}
                className="cursor-pointer"
                style={{ opacity }}
              >
                <motion.g
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: isHovered || isSelectedNode ? 1.2 : 1 }}
                  transition={{ duration: 0.45, delay, ease: easeOut }}
                >
                  {/* Glow */}
                  {(isHovered || isSelectedNode) && (
                    <circle r={radius + 10} fill={color} style={{ filter: 'blur(6px)' }} opacity={0.4} />
                  )}
                  {/* Hub glow ring */}
                  {node.isHub && (
                    <circle
                      r={radius + 8}
                      fill="none"
                      stroke={color}
                      strokeWidth={1}
                      strokeOpacity={0.3}
                      style={{ filter: 'blur(4px)' }}
                    />
                  )}
                  <circle
                    r={radius}
                    fill={node.isHub ? color : `${color}cc`}
                    stroke={isSelectedNode ? '#ffffff' : color}
                    strokeWidth={isSelectedNode ? 2 : 1.2}
                    strokeOpacity={isSelectedNode ? 0.95 : 0.5}
                  />
                  <text
                    y={radius + 16}
                    textAnchor="middle"
                    fontSize={11}
                    fontWeight={700}
                    fill="rgba(255,255,255,0.85)"
                    fontFamily="'Hanken Grotesk', sans-serif"
                    style={{ opacity: isHovered || isSelectedNode ? 1 : 0.75 }}
                  >
                    {node.label}
                  </text>
                </motion.g>
              </g>
            );
          })}
        </g>
      </svg>

      <div className="pointer-events-none absolute inset-x-0 bottom-5 flex justify-center px-4">
        <span className="pointer-events-auto rounded-full border border-white/10 bg-white/[0.05] px-4 py-2 text-center text-[10.5px] text-muted-foreground backdrop-blur-md transition-colors hover:border-white/20 sm:text-[11px]">
          Scroll to zoom · Drag canvas to pan · Double-click to reset · Click node to inspect
        </span>
      </div>

      <div className="absolute right-4 top-4 flex gap-2">
        <button
          onClick={resetView}
          title="Fit to screen"
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-background/70 text-muted-foreground backdrop-blur transition-all duration-200 hover:border-white/25 hover:text-foreground hover:bg-background/90"
        >
          <Maximize2 className="h-4 w-4" />
        </button>
        <button
          onClick={toggleFullscreen}
          title="Expand"
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-background/70 text-muted-foreground backdrop-blur transition-all duration-200 hover:border-white/25 hover:text-foreground hover:bg-background/90"
        >
          <Expand className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
